<!-- Splash.vue -->
<template>
  <transition name="oh3-splash-fade" @after-leave="$emit('finish')">
    <div
        v-if="show"
        class="oh3-splash"
        :class="{ 'is-dismissing': dismissing }"
        aria-label="OH3 loading"
        :style="{ '--oh3-fade-ms': fadeMs + 'ms' }"
    >
      <div class="oh3-splash-inner">
        <img :src="logoSrc" alt="OH3" class="oh3-logo" />
        <div class="oh3-tagline">Open-Hinata 3</div>
        <!-- mapReady待ちのプログレスインジケーター -->
        <div v-if="showProgress" class="oh3-progress">
          <div class="oh3-progress-bar"></div>
        </div>
      </div>

      <button
          class="oh3-skip"
          @mousedown="pressed = true" @mouseup="pressed = false" @mouseleave="pressed = false"
          @touchstart.passive="pressed = true" @touchend.passive="pressed = false">
        Skip
      </button>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'Splash',
  props: {
    logoSrc:      { type: String,  default: '/ogp.png' },
    /** 最小表示時間(ms) — これより早くは消えない */
    minMs:        { type: Number,  default: 800 },
    /** 最大表示時間(ms) — mapReadyが来なくても強制的に消える */
    maxMs:        { type: Number,  default: 3000 },
    /** 地図準備完了フラグ */
    mapReady:     { type: Boolean, default: false },
    /** 自動で閉じるかどうか */
    autoHide:     { type: Boolean, default: true },
    /** フェード時間(ms) */
    fadeMs:       { type: Number,  default: 800 }
  },
  data () {
    return {
      show: true,
      dismissing: false,
      pressed: false,
      tMax: null,
      tHold: null,
      minElapsed: false,   // 最小時間が経過したか
      mountedAt: 0,
      showProgress: false  // プログレス表示（min経過後まだready未達の場合）
    }
  },
  watch: {
    pressed (val) {
      clearTimeout(this.tHold)
      if (val) this.tHold = setTimeout(() => this.hide(), 700)
    },
    mapReady (ready) {
      if (ready && this.autoHide) this.tryHide()
    }
  },
  mounted () {
    this.mountedAt = Date.now()

    // 最小時間後にフラグを立て、readyなら閉じる
    setTimeout(() => {
      this.minElapsed = true
      if (this.mapReady && this.autoHide) {
        this.hide()
      } else if (!this.mapReady) {
        // まだ準備中 → プログレス表示
        this.showProgress = true
      }
    }, this.minMs)

    // 最大時間で強制的に閉じる
    if (this.autoHide) {
      this.tMax = setTimeout(() => this.hide(), this.maxMs)
    }
  },
  beforeUnmount () {
    clearTimeout(this.tMax)
    clearTimeout(this.tHold)
  },
  methods: {
    tryHide () {
      if (this.minElapsed) {
        this.hide()
      }
      // minElapsed=false の場合はmin経過後にwatchかsetTimeoutで閉じる
    },
    hide () {
      if (!this.show || this.dismissing) return
      this.dismissing = true
      clearTimeout(this.tMax)
      setTimeout(() => { this.show = false }, this.fadeMs)
    }
  }
}
</script>

<style scoped>
.oh3-splash {
  position: fixed; inset: 0; display: grid; place-items: center;
  background: radial-gradient(1200px 1200px at 50% 50%, rgba(255,255,255,.95), rgba(240,242,245,1));
  z-index: 9999;
  transition: opacity var(--oh3-fade-ms, 320ms) ease;
  pointer-events: none; /* 下へスルー */
}
.oh3-splash.is-dismissing { opacity: 0; }

.oh3-splash-inner {
  display: grid; place-items: center; gap: 10px;
  transform: scale(.98);
  animation: oh3-pop 900ms cubic-bezier(.2,.8,.2,1) both;
}

.oh3-logo {
  max-width: min(70vw, 520px);
  max-height: min(38vh, 260px);
  object-fit: contain;
  user-select: none; pointer-events: none;
  filter: drop-shadow(0 8px 18px rgba(0,0,0,.08));
}
.oh3-tagline { font-weight: 600; letter-spacing: .08em; color: #475569; opacity: .95; }

/* プログレスバー */
.oh3-progress {
  width: min(60vw, 320px);
  height: 3px;
  background: rgba(0,0,0,.08);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 8px;
}
.oh3-progress-bar {
  height: 100%;
  background: rgba(71,85,105,.5);
  border-radius: 2px;
  animation: oh3-progress-anim 2.2s ease-in-out infinite;
}
@keyframes oh3-progress-anim {
  0% { width: 0; margin-left: 0; }
  50% { width: 60%; margin-left: 20%; }
  100% { width: 0; margin-left: 100%; }
}

@keyframes oh3-pop {
  0% { transform: scale(.94); opacity: 0 }
  60% { transform: scale(1.02); opacity: 1 }
  100% { transform: scale(1) }
}

.oh3-skip {
  position: absolute; right: 14px; bottom: 12px;
  background: rgba(0,0,0,.06); border: 1px solid rgba(0,0,0,.12);
  border-radius: var(--oh3-radius-full); padding: 6px 12px; color: #64748b; font-weight: 600; letter-spacing: .04em;
  pointer-events: auto; /* このボタンは押せる */
}
.oh3-skip:active { transform: scale(.98); }

.oh3-splash-fade-enter-active,
.oh3-splash-fade-leave-active { transition: opacity var(--oh3-fade-ms, 320ms) ease; }
.oh3-splash-fade-enter-from,
.oh3-splash-fade-leave-to { opacity: 0; }
</style>
